import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import { canTransition } from '../utils/billing';
import { DEFAULT_SETTINGS, diffItems, itemInsertRow, newId } from '../utils/quotes';
import { normalizeTestNumber } from '../utils/botTestMode';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];
const PAGE = 1000;
const LIST_LIMIT = 500;

const isMissingTable = (error) =>
  ['42P01', 'PGRST205'].includes(error?.code) || /quotes|quote_items|billing_settings/i.test(error?.message || '');

// Los mensajes de las reglas de la base ya están en español y dicen qué pasó; los demás errores no se muestran tal cual.
const explain = (error, fallback) => {
  // Un 42501 de la seguridad por filas o de los permisos por columna viene en inglés: se cambia por una frase clara.
  if (error?.code === '42501' && /row-level security|permission denied/i.test(error.message || '')) return 'No tienes permiso sobre las cotizaciones de este negocio (solo el dueño o un administrador).';
  return ['22023', '42501'].includes(error?.code) && error?.message ? error.message : fallback;
};

const fetchCustomers = async (business) => {
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase.from('customers').select('*').eq('business_id', business).order('name').range(from, from + PAGE - 1);
    if (error) return { error };
    rows.push(...(data || []));
    if (!data || data.length < PAGE) return { data: rows };
  }
};

// Cotizaciones del negocio: lista, editor, estados, versiones y ajustes de facturación.
// La base calcula el número, la versión, los totales y la auditoría: aquí solo se envían las columnas permitidas (utils/quotes.js).
export const useQuotes = ({ withList = true } = {}) => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';

  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [settingsSaved, setSettingsSaved] = useState(false);
  // La columna del envío automático la agrega la migración 20261003e: mientras la base no la tenga, no se envía en el guardado (si no, no se podrían guardar los ajustes).
  const sendColumnKnown = useRef(false);
  // Igual con el número de aviso de cambios (migración 20261005a).
  const notifyColumnKnown = useRef(false);
  // Y con el sufijo de la numeración (migración 20261005c).
  const suffixColumnKnown = useRef(false);
  // Próximo número de cotización y de venta (último usado + 1). Se leen de los contadores; solo la base los cambia.
  const [nextNumbers, setNextNumbers] = useState({ quote: 1, sale: 1 });
  // Solicitudes de cambio abiertas (las que el cliente pidió por WhatsApp y nadie ha atendido), por cotización. Vacío si falta la migración.
  const [openChanges, setOpenChanges] = useState({});
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId) { setLoading(false); return; }
    setLoading(true);
    const business = String(activeBusinessId);
    const [q, c, s, ch, dc] = await Promise.all([
      withList
        ? supabase.from('quotes').select('*').eq('business_id', business).order('created_at', { ascending: false }).range(0, LIST_LIMIT - 1)
        : Promise.resolve({ data: [], error: null }),
      fetchCustomers(business),
      supabase.from('billing_settings').select('*').eq('business_id', business).maybeSingle(),
      withList
        ? supabase.from('quote_change_requests').select('*').eq('business_id', business).eq('status', 'nueva')
        : Promise.resolve({ data: [], error: null }),
      supabase.from('document_counters').select('kind,last_number').eq('business_id', business)
    ]);
    const error = q.error || s.error;
    if (error) {
      setTableMissing(isMissingTable(error));
      setQuotes([]);
    } else {
      setTableMissing(false);
      setQuotes(q.data || []);
      setSettings({ ...DEFAULT_SETTINGS, ...(s.data || {}) });
      sendColumnKnown.current = Boolean(s.data && 'auto_send_on_approval' in s.data);
      notifyColumnKnown.current = Boolean(s.data && 'notify_phone' in s.data);
      suffixColumnKnown.current = Boolean(s.data && 'quote_suffix' in s.data);
      const last = (kind) => Number((dc.data || []).find((r) => r.kind === kind)?.last_number) || 0;
      setNextNumbers({ quote: last('quote') + 1, sale: last('sale') + 1 });
      setSettingsSaved(Boolean(s.data));
    }
    setOpenChanges(ch.error ? {} : Object.fromEntries((ch.data || []).map((r) => [r.quote_id, r])));
    setCustomers(c.error ? [] : c.data);
    setLoading(false);
  }, [activeBusinessId, withList]);

  useEffect(() => { load(); }, [load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  // Una cotización con sus líneas, tal como están guardadas.
  const getQuote = useCallback(async (id) => {
    const [q, i] = await Promise.all([
      supabase.from('quotes').select('*').eq('id', id).eq('business_id', String(activeBusinessId)).maybeSingle(),
      supabase.from('quote_items').select('*').eq('quote_id', id).order('position')
    ]);
    if (q.error || i.error) { toast.error('No se pudo abrir la cotización.'); return null; }
    if (!q.data) return null;
    return { quote: q.data, items: i.data || [] };
  }, [activeBusinessId]);

  const insertItems = async (quoteId, lines) => {
    if (!lines.length) return null;
    const business = String(activeBusinessId);
    const rows = lines.map(({ line, position }) => itemInsertRow(line, quoteId, business, position));
    const { error } = await supabase.from('quote_items').insert(rows);
    return error || null;
  };

  // Crea la cotización con sus líneas. El número lo asigna la base al guardar (por eso no se crean borradores vacíos).
  const createQuote = async ({ customer_id, price_list_key, valid_until, notes, lines, source = 'manual', replaces_id = null }) => {
    if (guardDemo()) return null;
    const row = {
      id: newId(), business_id: String(activeBusinessId), replaces_id, customer_id: customer_id || null, price_list_key: price_list_key || null,
      source, valid_until: valid_until || null, notes: String(notes ?? '').trim() || null
    };
    const { data, error } = await supabase.from('quotes').insert([row]).select().single();
    if (error) {
      toast.error(error.code === '23505' ? 'Ya existe una versión nueva de esta cotización.' : explain(error, 'No se pudo crear la cotización.'));
      return null;
    }
    const itemError = await insertItems(data.id, (lines || []).map((line, position) => ({ line, position })));
    if (itemError) toast.error('La cotización se creó, pero no se pudieron guardar las líneas. Ábrela y vuelve a guardar.');
    return getQuote(data.id);
  };

  // Guarda un borrador: los datos de la cabecera y solo las líneas que cambiaron (nuevas, editadas, borradas).
  const saveQuote = async (quote, fields, lines, savedItems) => {
    if (guardDemo()) return null;
    if (quote.status !== 'borrador') { toast.error('Solo un borrador se puede editar: crea una nueva versión.'); return null; }
    const header = {
      customer_id: fields.customer_id || null, price_list_key: fields.price_list_key || null,
      valid_until: fields.valid_until || null, notes: String(fields.notes ?? '').trim() || null
    };
    const headerChanged = Object.entries(header).some(([k, v]) => (quote[k] ?? null) !== v);
    if (headerChanged) {
      const { error } = await supabase.from('quotes').update(header).eq('id', quote.id);
      if (error) { toast.error(explain(error, 'No se pudo guardar la cotización.')); return null; }
    }
    const { toInsert, toUpdate, toDelete } = diffItems(savedItems, lines);
    const fail = (error) => { toast.error(explain(error, 'No se pudieron guardar las líneas. Revisa y vuelve a intentar.')); return null; };
    const insertError = await insertItems(quote.id, toInsert);
    if (insertError) return fail(insertError);
    for (const { id, row } of toUpdate) {
      const { error } = await supabase.from('quote_items').update(row).eq('id', id);
      if (error) return fail(error);
    }
    if (toDelete.length) {
      const { error } = await supabase.from('quote_items').delete().in('id', toDelete);
      if (error) return fail(error);
    }
    toast.success('Cotización guardada');
    return getQuote(quote.id);
  };

  // Cambia el estado. La base valida la transición, el total y quién lo hizo.
  const transition = async (quote, to) => {
    if (guardDemo()) return null;
    if (!canTransition(quote.status, to)) { toast.error('Ese cambio de estado no está permitido.'); return null; }
    const { error } = await supabase.from('quotes').update({ status: to }).eq('id', quote.id);
    if (error) { toast.error(explain(error, 'No se pudo cambiar el estado.')); return null; }
    return getQuote(quote.id);
  };

  // Una cotización enviada no se edita: se crea la versión siguiente (mismo número) y la anterior queda reemplazada.
  const newVersion = async (quote, items) => {
    if (guardDemo()) return null;
    if (quote.status !== 'enviada') { toast.error('Solo una cotización enviada puede tener una nueva versión (un borrador se edita directamente).'); return null; }
    const created = await createQuote({
      customer_id: quote.customer_id, price_list_key: quote.price_list_key, valid_until: null, notes: quote.notes, source: quote.source,
      replaces_id: quote.id, lines: items.map((i) => ({ ...i, id: newId() }))
    });
    if (!created) return null;
    const { error } = await supabase.from('quotes').update({ status: 'reemplazada' }).eq('id', quote.id);
    if (error) toast.error('La nueva versión quedó como borrador, pero la anterior no se marcó como reemplazada. Márcala a mano.');
    return created;
  };

  // Envíos por WhatsApp de una cotización (los más recientes primero). Los escribe la base y los procesa n8n; la app solo los lee.
  const getDeliveries = useCallback(async (quoteId) => {
    const { data, error } = await supabase.from('quote_deliveries').select('*').eq('quote_id', quoteId).order('created_at', { ascending: false }).limit(5);
    if (error) return null;
    return data || [];
  }, []);

  // Pone la cotización (aprobada o ya enviada) en la cola de envío por WhatsApp. Si ya hay un envío abierto, devuelve ese.
  const requestSend = async (quote) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('request_quote_send', { p_quote: quote.id });
    if (error) { toast.error(explain(error, 'No se pudo poner en cola el envío.')); return false; }
    return true;
  };

  // Solicitudes de cambio de una cotización (la más reciente primero). null si falta la migración o no se pudo leer.
  const getChangeRequests = useCallback(async (quoteId) => {
    const { data, error } = await supabase.from('quote_change_requests').select('*').eq('quote_id', quoteId).order('created_at', { ascending: false }).limit(5);
    if (error) return null;
    return data || [];
  }, []);

  // La persona marca como atendida la solicitud de cambio (también se cierra sola al crear la versión nueva).
  const resolveChangeRequest = async (id) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('resolve_quote_change_request', { p_id: id });
    if (error) { toast.error(explain(error, 'No se pudo marcar como atendida.')); return false; }
    setOpenChanges((cur) => Object.fromEntries(Object.entries(cur).filter(([, r]) => r.id !== id)));
    return true;
  };

  // Copia una cotización (en cualquier estado) como borrador nuevo con otro número.
  const duplicateQuote = async (quote, items) =>
    createQuote({
      customer_id: quote.customer_id, price_list_key: quote.price_list_key, valid_until: null, notes: quote.notes,
      lines: items.map((i) => ({ ...i, id: newId() }))
    });

  const saveSettings = async (values) => {
    if (guardDemo()) return false;
    const row = {
      business_id: String(activeBusinessId),
      currency: values.currency || 'COP',
      quote_valid_days: Number(values.quote_valid_days) || 15,
      quote_prefix: String(values.quote_prefix || 'COT').toUpperCase(),
      sale_prefix: String(values.sale_prefix || 'VTA').toUpperCase(),
      legal_name: String(values.legal_name ?? '').trim() || null, tax_id: String(values.tax_id ?? '').trim() || null,
      address: String(values.address ?? '').trim() || null, phone: String(values.phone ?? '').trim() || null,
      email: String(values.email ?? '').trim() || null, footer_text: String(values.footer_text ?? '').trim() || null,
      auto_approve: Boolean(values.auto_approve),
      auto_approve_max_amount: values.auto_approve_max_amount === '' || values.auto_approve_max_amount == null ? null : Number(values.auto_approve_max_amount),
      stock_policy: values.stock_policy || 'warn',
      updated_at: new Date().toISOString()
    };
    if (!/^[A-Z0-9]{1,8}$/.test(row.quote_prefix) || !/^[A-Z0-9]{1,8}$/.test(row.sale_prefix)) { toast.error('El prefijo usa de 1 a 8 letras o números, sin espacios (ej. COT).'); return false; }
    const suffixes = { quote_suffix: String(values.quote_suffix ?? '').trim().toUpperCase(), sale_suffix: String(values.sale_suffix ?? '').trim().toUpperCase() };
    if (!/^[A-Z0-9]{0,8}$/.test(suffixes.quote_suffix) || !/^[A-Z0-9]{0,8}$/.test(suffixes.sale_suffix)) { toast.error('El sufijo usa hasta 8 letras o números, sin espacios ni guiones (ej. 2026).'); return false; }
    const wanted = {};
    for (const kind of ['quote', 'sale']) {
      const raw = values[`${kind}_next_number`];
      if (raw === '' || raw == null || Number(raw) === nextNumbers[kind]) continue;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 1 || n > 99999999) { toast.error('El número inicial debe ser un entero entre 1 y 99.999.999.'); return false; }
      wanted[kind] = n;
    }
    if (row.quote_valid_days < 1 || row.quote_valid_days > 365) { toast.error('La vigencia va de 1 a 365 días.'); return false; }
    if (row.auto_approve && !(row.auto_approve_max_amount > 0)) { toast.error('Para aprobar solas las cotizaciones del bot hay que fijar un tope mayor que 0.'); return false; }
    if (row.auto_approve_max_amount !== null && !(row.auto_approve_max_amount > 0)) { toast.error('El tope debe ser mayor que 0.'); return false; }
    if (values.auto_send_on_approval || sendColumnKnown.current) row.auto_send_on_approval = Boolean(values.auto_send_on_approval);
    if (suffixes.quote_suffix || suffixes.sale_suffix || suffixColumnKnown.current) Object.assign(row, suffixes);
    const rawNotify = String(values.notify_phone ?? '').trim();
    const notify = rawNotify ? normalizeTestNumber(rawNotify) : null;
    if (rawNotify && !notify) { toast.error('El número de aviso no es válido: escribe el celular con indicativo (ej. 573016867636).'); return false; }
    if (notify || notifyColumnKnown.current) row.notify_phone = notify;
    const { error } = await supabase.from('billing_settings').upsert([row], { onConflict: 'business_id' });
    if (error) {
      toast.error(/_suffix/i.test(error.message || '') ? 'Falta ejecutar la migración 20261005c_document_numbering.sql en Supabase para guardar el sufijo.' : /notify_phone/i.test(error.message || '') ? 'Falta ejecutar la migración 20261005a_quote_change_requests.sql en Supabase para guardar el número de aviso.' : 'No se pudieron guardar los ajustes.');
      return false;
    }
    if ('auto_send_on_approval' in row) sendColumnKnown.current = true;
    if ('notify_phone' in row) notifyColumnKnown.current = true;
    if ('quote_suffix' in row) suffixColumnKnown.current = true;
    setSettings({ ...DEFAULT_SETTINGS, ...row, notify_phone: row.notify_phone ?? '' });
    setSettingsSaved(true);
    // El número inicial se fija con la función de la base (ya con el prefijo y sufijo recién guardados): ella rechaza un número que ya se usó.
    for (const [kind, n] of Object.entries(wanted)) {
      const { error: numError } = await supabase.rpc('set_document_next_number', { p_business: String(activeBusinessId), p_kind: kind, p_next: n });
      if (numError) {
        toast.error(numError.code === '22023' && numError.message ? numError.message : /set_document_next_number|function/i.test(numError.message || '') ? 'Falta ejecutar la migración 20261005c_document_numbering.sql en Supabase para cambiar el número inicial.' : 'Los ajustes se guardaron, pero no se pudo cambiar el número inicial.');
        return false;
      }
      setNextNumbers((cur) => ({ ...cur, [kind]: n }));
    }
    toast.success('Ajustes guardados');
    return true;
  };

  return {
    quotes, customers, settings, settingsSaved, loading, tableMissing, isReadOnlyDemo, openChanges, nextNumbers,
    reload: load, getQuote, createQuote, saveQuote, transition, newVersion, duplicateQuote, saveSettings, getDeliveries, requestSend, getChangeRequests, resolveChangeRequest
  };
};
